/**
 * Thin webhook client. Every Ashraya question goes through here.
 *
 * The envelope is FIXED — designed once, never re-shaped. It supports text
 * today and voice/video later without breaking the host app's integration.
 *
 * `headers` is an optional map merged into the request headers — used when a
 * host points the widget directly at a secured backend (e.g. an X-Ashraya-Secret
 * shared secret) instead of an open n8n webhook. For the standard n8n-conductor
 * setup it is omitted.
 */

export async function sendAshraryaMessage({ webhookUrl, payload, headers, timeoutMs = 60000 }) {
  if (!webhookUrl) throw new Error('Ashraya: webhookUrl missing — provider misconfigured')
  const controller = new AbortController()
  // Friendly timeout — the full pipeline (Embed -> Vector Search -> Fetch
  // Articles -> Build Prompt -> LLM -> Parse -> Save Session -> Save Messages
  // -> Respond) can take 25-40s especially when the LLM is generating a long
  // tour JSON. 60s gives plenty of headroom while still failing fast if the
  // backend is genuinely stuck.
  const timer = setTimeout(() => controller.abort(new Error('Ashraya request timed out after 60s — the backend is taking longer than expected. Please try again.')), timeoutMs)
  try {
    const res = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(headers || {}) },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`Ashraya webhook returned ${res.status}: ${text.slice(0, 200)}`)
    }
    return await res.json()
  } catch (err) {
    // Re-throw with a friendlier message when the abort fired due to timeout
    // (browsers report "signal is aborted without reason" which is opaque).
    if (err?.name === 'AbortError') {
      throw new Error('Ashraya took too long to respond. The help bot may be warming up — please try again in a moment.')
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}
